import { notify } from '@eleveo/components-react';
import parseMilliseconds from 'parse-ms';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EventsType, useIdleTimer } from 'react-idle-timer';
import { useInterval } from 'usehooks-ts';

import { useAuth } from './AuthorizationProvider';
import { useConfig } from './ConfigurationProvider';
import { MILLIS_50, ONE_SECOND } from '../constants/timeConstants';
import { sessionStateMock } from '../utils/test/sessionProviderMock';
import { timePartsToHumanReadableString } from '../utils/timeUtils';
// eslint-disable-next-line import/no-cycle

const NON_IDLE_EVENTS: EventsType[] = ['keydown', 'mousedown', 'touchstart'];
const SESSION_EXPIRATION_TOAST_ID = 'SESSION_EXPIRATION_TOAST_ID';

export const SessionProviderContext = React.createContext(sessionStateMock);

/**
 * Provides session expiration functionality to rest of the application. It has to be used within auth context in order
 * to extract necessary information about session idle time (`sessionIdleTimeDuration`). This parameter is computed
 * inside AuthorizationProvider check {@link getSessionTimeoutFromRefreshToken} for implementation details.
 *
 * In special cases this value can be overridden by `setCustomSessionIdleTime` function received from this hook.
 *
 * User is notified about session expiration prior to logging out by toast notification. Notification duration is set by
 * default to 3 minutes before logging out but when session expiration time is shorter than notification duration than
 * 80% of expiration time is used. Expiration time can be modified by editing `SSO Session Idle` parameter in Keycloak
 * admin interface under Realm Settings > Tokens.
 * Notification time can be configured in `.env` file of your environment under `REACT_APP_NOTIFICATION_DURATION` key.
 */
export const SessionProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const authContext = useAuth();
  if (!authContext) throw new Error('SessionProvider has to be used inside auth context!');

  const { notificationDuration } = useConfig();
  const { sessionIdleTimeDuration: sessionIdleTimeDurationConfigured, logout } = authContext;
  const { t } = useTranslation();

  const lastUpdate = useRef<number | null>(null);
  const [customSessionIdle, setCustomSessionIdle] = useState<number | undefined>(undefined);
  const [logoutInterval, setLogoutInterval] = useState<number | null>(null);

  /**
   * @param {number} time - custom session timeout in millis
   */
  const setCustomSessionIdleTimeFromConsumer = useCallback((time: number) => {
    if (time == null || Number.isNaN(time) || time <= 0) {
      console.warn('Custom session timeout was not applied, incorrect value was received!');
      return;
    }
    setCustomSessionIdle(time);
  }, []);

  // time after which user will be logged out
  const sessionIdleTimeDuration = customSessionIdle || sessionIdleTimeDurationConfigured;

  // time how long is notification present before logging out,
  const toastNotificationDuration =
    sessionIdleTimeDuration > notificationDuration
      ? notificationDuration
      : Math.ceil(Math.round(sessionIdleTimeDuration * 0.8) / 10000) * 10000;

  const createToastContent = (duration: number) => {
    const timeCaption = timePartsToHumanReadableString(parseMilliseconds(duration), t);
    return (
      <div>
        {t('session.notifyLogout')}
        <p>{timeCaption}</p>
      </div>
    );
  };

  const promptUser = () => {
    notify.warning(createToastContent(toastNotificationDuration), {
      autoClose: toastNotificationDuration,
      toastId: SESSION_EXPIRATION_TOAST_ID,
      progressClassName: 'Toastify__progress-bar--animated',
    });

    // will continue update notification content
    setLogoutInterval(MILLIS_50);
  };

  const resetTimers = () => {
    notify.dismiss(SESSION_EXPIRATION_TOAST_ID);
    setLogoutInterval(null);
    lastUpdate.current = null;
    // eslint-disable-next-line @typescript-eslint/no-use-before-define,no-unused-expressions
    idleTimer && idleTimer.reset();
  };

  const logoutIdleUser = () => {
    /* eslint-disable no-console */
    console.debug(`User is being logged out due to idle timeout.`);
    console.debug(`Session idle time is set to ${customSessionIdle || sessionIdleTimeDuration}`);

    setLogoutInterval(null);
    lastUpdate.current = null;

    logout();
    /* eslint-enable no-console */
  };

  // `onIdle` is called after `timeout`, `onPrompted` is called before `promptBeforeIdle` ms before `timeout`
  const idleTimer = useIdleTimer({
    timeout: customSessionIdle || sessionIdleTimeDuration,
    promptBeforeIdle: toastNotificationDuration,
    onPrompt: promptUser,
    onAction: resetTimers,
    onIdle: logoutIdleUser,
    events: NON_IDLE_EVENTS,
    throttle: 1000, // reset timers only once a second
  });

  useInterval(() => {
    const date = new Date();
    const milliseconds = date.getMilliseconds();
    const timeFromLastUpdate = date.getTime() - (lastUpdate.current || 0);
    // skip update when it is not first 50ms of second and previous update was not within 1s
    if (milliseconds > MILLIS_50 && timeFromLastUpdate < ONE_SECOND) {
      return;
    }
    lastUpdate.current = date.getTime() - milliseconds;

    const remainingTimePrecise = idleTimer.getRemainingTime();
    if (remainingTimePrecise <= 0) {
      setLogoutInterval(null);
      lastUpdate.current = null;
      return;
    }

    const remainingTimePreciseToDisplay = Math.round(remainingTimePrecise / 1000) * 1000;

    notify.update(SESSION_EXPIRATION_TOAST_ID, {
      render: createToastContent(remainingTimePreciseToDisplay),
      progress: remainingTimePrecise / toastNotificationDuration,
    });
  }, logoutInterval);

  return (
    <SessionProviderContext.Provider
      value={{ setCustomSessionIdleTime: setCustomSessionIdleTimeFromConsumer, ...idleTimer }}
    >
      {children}
    </SessionProviderContext.Provider>
  );
};

export const SessionProviderConsumer = SessionProviderContext.Consumer;

export const useSession = () => React.useContext(SessionProviderContext);
